import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { LuArrowUp, LuArrowDown, LuChevronRight, LuCalendar, LuExternalLink, LuImage } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import { useChartTokens, useTooltipStyle } from '../components/ChartKit';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import { useAltura } from '../hooks/useAltura';
import { OpportunityService } from '../services/OpportunityService';
import type { Opportunity } from '../services/OpportunityService';
import { TipoContatoService, type TipoContato } from '../services/TipoContatoService';
import { capaDoAnuncio, ehAnuncioPago, linkDoAnuncio, nomeDoAnuncio, palavraDaOrigem } from '../services/TrafegoPagoService';
import PainelLista, { type LinhaLista, type ListaAberta } from './PainelLista';
import TourNovidade from '../components/TourNovidade';
import { TOUR_PAINEL } from '../config/novidades';
import { temFuncionalidade } from '../config/permissions';
import { textoOrigemMarketing } from '../utils/origemMarketing';
import { palavraDoCanal, nomeEtapa } from '../data/etapas';
import { PARADOS, diasDoCorte } from '../data/semInteracao';
import {
    FiltrosBotao, FiltrosResumo, FiltrosPainel, FiltroColuna, FiltroCampo, FiltroChips, FiltroMulti,
    type FiltroOpcao,
} from '../components/ListKit';
import '../components/ListKit.css';
// Dashboard.css é global disfarçado: define .page-header, .dashboard-card e
// .table-container, consumidos por 24+ telas que ainda não migraram. Sai do
// bundle se este import sumir. Ver docs/designsystem/estado-do-redesign.md.
import './Dashboard.css';
import './PainelOperacional.css';

/* ─── Regras de domínio ───────────────────────────────────────── */

/* O rótulo da etapa tem um dono só (`data/etapas`), que já traz o nome da empresa. Este mapa
   era uma sétima cópia, e uma etapa criada depois cairia no `?? descricaoStatus` da API. */
const STATUS_LABEL = new Proxy({} as Record<number, string>, {
    /* Devolve `undefined` quando não conhece a etapa, para o `?? op.descricaoStatus` de quem lê
       continuar valendo — `nomeEtapa` responderia "—" e engoliria o nome que a API mandou. */
    get: (_, chave) => {
        const nome = nomeEtapa(Number(chave));
        return nome === '—' ? undefined : nome;
    },
});

const STATUS_GANHO = 3;
const STATUS_PERDIDO = 6;
/** A etapa em que quem responde é o Brik AI. A 8 ("IA Encerrada") já é trabalho de gente. */
const STATUS_IA = 1;

/** Aberta = ainda dá trabalho. IA Encerrada entra: é justamente a que espera gente. */
const estaAberta = (op: Opportunity) =>
    op.idStauts !== STATUS_GANHO && op.idStauts !== STATUS_PERDIDO;

/** Quantas linhas de "precisa de ação" aparecem antes de expandir. */
const LINHAS_INICIAIS = 6;

const DIA = 86_400_000;

const TEMPERATURA: Record<number, string> = { 1: 'Quente', 2: 'Morno', 3: 'Frio' };

/* ─── Datas ───────────────────────────────────────────────────── */

const inicioDoDia = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const fimDoDia = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

const dentroDe = (iso: string | null | undefined, de: Date, ate: Date) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return Number.isFinite(t) && t >= de.getTime() && t <= ate.getTime();
};

const ddmm = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

/* ─── Formatação ──────────────────────────────────────────────── */

const brl = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const umaCasa = (v: number) =>
    v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/* ─── Período ─────────────────────────────────────────────────── */

type PresetId = 'hoje' | '7d' | '30d' | 'datas';

const PRESETS: { id: PresetId; label: string; dias: number }[] = [
    { id: 'hoje', label: 'Hoje', dias: 1 },
    { id: '7d', label: '7 dias', dias: 7 },
    { id: '30d', label: '30 dias', dias: 30 },
];

/* ─── Filtros ─────────────────────────────────────────────────── */

const SEM_RESPONSAVEL = 'Sem responsável';
const SEM_ORIGEM = 'Sem origem';
const SEM_TIPO = 'Sem tipo';
const SEM_ORGANIZACAO = 'Sem organização';

const COM_QUEM = [{ valor: 'Brik AI' }, { valor: 'Humano' }];
const CAMPANHAS = [{ valor: 'Orgânica', rotulo: 'Sem campanha' }, { valor: 'Campanha', rotulo: 'De campanha' }];
const AGUARDANDO = 'Aguardando resposta';

/** Cliente = 0 no OrigemMensagemEnum da API: ele falou por último e ninguém respondeu. */
const ORIGEM_CLIENTE = 0;

/**
 * O recorte de trabalho da tela. Vive na sessão ([[filtro-nao-se-perde-ao-voltar]]): quem filtra
 * por um vendedor, abre a ficha de um negócio parado e volta, encontra o mesmo recorte.
 *
 * O corte padrão de "sem interação" é **7 dias**, o mais perto do que a tela fazia antes (mais de
 * 5 dias, fixo no código): quem já usava o Painel não vê a lista inchar sozinha de um dia para o
 * outro. Quem quer 24h ou 2 dias troca no painel, e a escolha fica.
 */
const RECORTE_VAZIO = {
    preset: '7d' as PresetId,
    de: '',
    ate: '',
    responsavel: [] as string[],
    etapa: [] as string[],
    comQuem: [] as string[],
    tipoContato: [] as string[],
    temperatura: [] as string[],
    organizacao: [] as string[],
    canal: [] as string[],
    origemLead: [] as string[],
    campanhaTipo: [] as string[],
    campanha: [] as string[],
    aguardando: false,
    semInteracao: '7 dias' as string | null,
};

interface Medida {
    entradas: number;
    ganhos: number;
    ganhoValor: number;
    perdidos: number;
    conversao: number;
}

/** Quem está com o negócio, do jeito que a tela escreve. A API manda "IA" quando não há ninguém. */
const donoDe = (op: Opportunity) => op.nomeUsuarioResponsavel?.trim() || SEM_RESPONSAVEL;

/* ─── Anúncios ────────────────────────────────────────────────── */

/** Quantos anúncios aparecem antes de expandir. */
const LINHAS_ANUNCIOS = 5;

/** A altura de sempre do gráfico de entradas, e o piso quando ele cresce ao lado de Anúncios. */
const ALTURA_ENTRADAS = 190;

interface LinhaAnuncio {
    chave: string;
    nome: string;
    /** "Instagram · vídeo" */
    apoio: string;
    capa: string | null;
    link: { url: string; rotulo: string } | null;
    entraram: number;
    ganhos: number;
    ganhoValor: number;
    /** As oportunidades por trás dos números da linha. */
    ops: Opportunity[];
}

interface Acumulado {
    chave: string;
    nome: string;
    tipo: string | null;
    publicacoes: Set<string>;
    redes: Set<string>;
    entraram: number;
    ganhos: number;
    ganhoValor: number;
    maisRecente: number;
    capa: string | null;
    capaEm: number;
    link: { url: string; rotulo: string } | null;
    linkEm: number;
    ops: Opportunity[];
}

const NOME_DA_REDE: Record<string, string> = { facebook: 'Facebook', instagram: 'Instagram' };

/** "Facebook e Instagram": o mesmo anúncio roda nas duas redes e cada lead diz de onde veio. */
const rotuloRedes = (redes: Set<string>) =>
    [...redes].map(r => NOME_DA_REDE[r] ?? r).sort().join(' e ') || null;

/**
 * Número que abre a lista do que ele contou (pedido dele, 17/10/2026). Sem hover: a pista é
 * o tracejado suave embaixo, sempre visível. Zero não abre nada, então não ganha tracejado.
 */
const Porta = ({ n, rotulo, onClick, children }: { n: number; rotulo: string; onClick: () => void; children: ReactNode }) =>
    n > 0
        ? <button type="button" className="op-porta" onClick={onClick} title={rotulo}>{children}</button>
        : <>{children}</>;

/** A capa que o Brik guarda. Sem capa (chegou antes de o Brik guardar) ou quebrada: quadrado neutro. */
const CapaAnuncio = ({ url }: { url: string | null }) => {
    const [quebrou, setQuebrou] = useState(false);
    return url && !quebrou
        ? <img className="op-anu-capa" src={url} alt="" loading="lazy" onError={() => setQuebrou(true)} />
        : <span className="op-anu-capa is-vazia" aria-hidden="true"><LuImage size={16} /></span>;
};

const Dashboard = () => {
    const celular = useCelular();
    const [todas, setTodas] = useState<Opportunity[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [mostrarTodasParadas, setMostrarTodasParadas] = useState(false);
    const [mostrarTodosAnuncios, setMostrarTodosAnuncios] = useState(false);
    const [lista, setLista] = useState<ListaAberta | null>(null);
    const [refEntradas, alturaEntradas] = useAltura<HTMLDivElement>();

    const tokens = useChartTokens();
    const tooltip = useTooltipStyle();

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const oportunidades = await OpportunityService.getAllOpportunities();
            setTodas(oportunidades || []);
            setError(null);
        } catch (err) {
            setError('Não foi possível carregar os dados do painel.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    /* ─── Recorte: período e filtros ─────────────────────────────── */

    const [inicial, gravarRecorte] = useRecorte('op:recorte', RECORTE_VAZIO);
    const [preset, setPreset] = useState<PresetId>(inicial.preset);
    const [dataDe, setDataDe] = useState(inicial.de);
    const [dataAte, setDataAte] = useState(inicial.ate);
    const [responsavel, setResponsavel] = useState<string[]>(inicial.responsavel);
    const [etapa, setEtapa] = useState<string[]>(inicial.etapa);
    const [comQuem, setComQuem] = useState<string[]>(inicial.comQuem);
    const [tipoContato, setTipoContato] = useState<string[]>(inicial.tipoContato);
    const [temperatura, setTemperatura] = useState<string[]>(inicial.temperatura);
    const [organizacao, setOrganizacao] = useState<string[]>(inicial.organizacao);
    const [canal, setCanal] = useState<string[]>(inicial.canal);
    const [origemLead, setOrigemLead] = useState<string[]>(inicial.origemLead);
    const [campanhaTipo, setCampanhaTipo] = useState<string[]>(inicial.campanhaTipo);
    const [campanha, setCampanha] = useState<string[]>(inicial.campanha);
    const [aguardando, setAguardando] = useState(inicial.aguardando);
    const [semInteracao, setSemInteracao] = useState<string | null>(inicial.semInteracao);

    const [filtrosAbertos, setFiltrosAbertos] = useState(false);
    const filtrosAncora = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        gravarRecorte({
            preset, de: dataDe, ate: dataAte, responsavel, etapa, comQuem, tipoContato, temperatura,
            organizacao, canal, origemLead, campanhaTipo, campanha, aguardando, semInteracao,
        });
    }, [preset, dataDe, dataAte, responsavel, etapa, comQuem, tipoContato, temperatura, organizacao,
        canal, origemLead, campanhaTipo, campanha, aguardando, semInteracao, gravarRecorte]);

    /* A lista de tipos muda pouco e serve só ao filtro: uma chamada por abertura da tela. */
    const usaTipoContato = temFuncionalidade('tipo_contato');
    const [tiposContato, setTiposContato] = useState<TipoContato[]>([]);
    useEffect(() => {
        if (!usaTipoContato) return;
        let vivo = true;
        TipoContatoService.getAll()
            .then(r => { if (vivo && r.sucesso) setTiposContato(r.dados || []); })
            .catch(() => { /* sem a lista o filtro não aparece; o painel abre igual */ });
        return () => { vivo = false; };
    }, [usaTipoContato]);

    /* ─── A base da tela: tudo o que sobrou dos filtros ──────────────
       Decisão dele (14/10/2026): o filtro vale para a TELA INTEIRA — KPIs, carga, gráficos e a
       lista de paradas. O período continua governando só os KPIs de fluxo, porque ele é janela de
       comparação e não recorte: um negócio parado há 40 dias entrou há 60, e recortar a base pela
       data de entrada esvaziaria justamente o bloco que diz o que precisa de ação. */

    const base = useMemo(() => todas.filter(op => {
        if (responsavel.length && !responsavel.includes(donoDe(op))) return false;
        if (etapa.length && !etapa.includes(STATUS_LABEL[op.idStauts] ?? op.descricaoStatus)) return false;
        if (comQuem.length && !comQuem.includes(op.idStauts === STATUS_IA ? 'Brik AI' : 'Humano')) return false;
        if (tipoContato.length && !tipoContato.includes(String(op.tipoContatoId ?? ''))) return false;
        if (temperatura.length && (!op.tipoLeadId || !temperatura.includes(TEMPERATURA[op.tipoLeadId]))) return false;
        if (organizacao.length && !organizacao.includes(op.nomeOrganizacao?.trim() || SEM_ORGANIZACAO)) return false;
        if (canal.length && !canal.includes(palavraDoCanal(op.idOrigemOportunidade))) return false;
        if (origemLead.length && !origemLead.includes(textoOrigemMarketing(op.origemMarketing) ?? SEM_ORIGEM)) return false;
        if (campanhaTipo.length && !campanhaTipo.includes(op.campanha ? 'Campanha' : 'Orgânica')) return false;
        if (campanha.length && (!op.campanha || !campanha.includes(op.nomeCampanha || 'Sem nome'))) return false;
        /* "Aguardando resposta" é quem falou por último, não quem está sem ler: abrir a conversa
           zera o não lido mesmo sem ninguém ter respondido. Negócio sem mensagem nenhuma (criado à
           mão) não aguarda nada. */
        if (aguardando && op.ultimaInteracaoOrigem !== ORIGEM_CLIENTE) return false;
        return true;
    }), [todas, responsavel, etapa, comQuem, tipoContato, temperatura, organizacao, canal, origemLead,
        campanhaTipo, campanha, aguardando]);

    /* ─── Opções dos filtros, medidas no universo da tela ───────────
       Opção que não recorta nada não aparece: um canal que ninguém usa ou um tipo de contato sem
       negócio viram chip que devolve lista vazia. */

    const contar = useCallback((chave: (op: Opportunity) => string | null | undefined): FiltroOpcao[] => {
        const mapa = new Map<string, number>();
        todas.forEach(op => {
            const v = chave(op);
            if (!v) return;
            mapa.set(v, (mapa.get(v) ?? 0) + 1);
        });
        return [...mapa.entries()].sort((a, b) => b[1] - a[1]).map(([valor, total]) => ({ valor, total }));
    }, [todas]);

    const opcoesResponsavel = useMemo(() => contar(donoDe), [contar]);
    const opcoesOrganizacao = useMemo(() => contar(op => op.nomeOrganizacao?.trim() || SEM_ORGANIZACAO), [contar]);
    const opcoesCampanha = useMemo(() => contar(op => (op.campanha ? op.nomeCampanha || 'Sem nome' : null)), [contar]);

    /* A etapa vai na ordem do funil, não na do volume: a lista é a mesma coisa que as colunas das
       Oportunidades, e reordenar por quantidade faria "Ganho" aparecer no meio. */
    const opcoesEtapa = useMemo((): FiltroOpcao[] => {
        const totais = new Map<string, number>();
        todas.forEach(op => {
            const r = STATUS_LABEL[op.idStauts] ?? op.descricaoStatus;
            totais.set(r, (totais.get(r) ?? 0) + 1);
        });
        return Object.values(STATUS_LABEL)
            .filter(r => totais.has(r))
            .map(valor => ({ valor, total: totais.get(valor)! }));
    }, [todas]);

    const opcoesTemperatura = useMemo(() => {
        const presentes = new Set(todas.map(op => (op.tipoLeadId ? TEMPERATURA[op.tipoLeadId] : null)).filter(Boolean));
        return Object.values(TEMPERATURA).filter(t => presentes.has(t)).map(valor => ({ valor }));
    }, [todas]);

    const opcoesCanal = useMemo(() => {
        const vistos = new Set(todas.map(op => palavraDoCanal(op.idOrigemOportunidade)));
        return [...vistos].map(valor => ({ valor }));
    }, [todas]);

    const opcoesTipoContato = useMemo((): FiltroOpcao[] => {
        const comTipo = tiposContato
            .map(t => ({ valor: String(t.tipoContatoId), rotulo: t.nome, total: todas.filter(op => op.tipoContatoId === t.tipoContatoId).length }))
            .filter(o => o.total > 0);
        const sem = todas.filter(op => !op.tipoContatoId).length;
        return sem && comTipo.length ? [...comTipo, { valor: '', rotulo: SEM_TIPO, total: sem }] : comTipo;
    }, [todas, tiposContato]);

    const opcoesOrigemLead = useMemo((): FiltroOpcao[] => {
        const comOrigem = contar(op => textoOrigemMarketing(op.origemMarketing));
        if (!comOrigem.length) return [];
        const sem = todas.filter(op => !textoOrigemMarketing(op.origemMarketing)).length;
        return sem ? [...comOrigem, { valor: SEM_ORIGEM, total: sem }] : comOrigem;
    }, [contar, todas]);

    /* ─── Janelas do período escolhido e do anterior, do mesmo tamanho ── */

    const janela = useMemo(() => {
        if (preset === 'datas' && (dataDe || dataAte)) {
            const de = inicioDoDia(dataDe ? new Date(dataDe + 'T00:00:00') : new Date(dataAte + 'T00:00:00'));
            const fim = fimDoDia(dataAte ? new Date(dataAte + 'T00:00:00') : new Date());
            const dias = Math.max(1, Math.round((fim.getTime() - de.getTime()) / DIA));
            const fimAnterior = fimDoDia(new Date(de.getTime() - DIA));
            const deAnterior = inicioDoDia(new Date(fimAnterior.getTime() - (dias - 1) * DIA));
            return { dias, de, fim, deAnterior, fimAnterior };
        }
        const dias = PRESETS.find(p => p.id === preset)?.dias ?? 7;
        const fim = fimDoDia(new Date());
        const de = inicioDoDia(new Date(Date.now() - (dias - 1) * DIA));
        const fimAnterior = fimDoDia(new Date(de.getTime() - DIA));
        const deAnterior = inicioDoDia(new Date(fimAnterior.getTime() - (dias - 1) * DIA));
        return { dias, de, fim, deAnterior, fimAnterior };
    }, [preset, dataDe, dataAte]);

    /* ─── KPIs de fluxo ────────────────────────────────────────────────
       Só fluxo tem comparação de período. Ganho segue a mesma regra do
       Analytics (status 3 + dataFechamentoVenda), senão os dois números
       divergem. Perda não tem data própria na API: cai para a entrada. */

    const medir = useCallback((de: Date, ate: Date): Medida => {
        const entradas = base.filter(op => dentroDe(op.dataConversaCriada, de, ate)).length;
        const ganhos = base.filter(op => op.idStauts === STATUS_GANHO && dentroDe(op.dataFechamentoVenda, de, ate));
        const perdidos = base.filter(op =>
            op.idStauts === STATUS_PERDIDO && dentroDe(op.dataFechamentoVenda ?? op.dataConversaCriada, de, ate)).length;

        return {
            entradas,
            ganhos: ganhos.length,
            ganhoValor: ganhos.reduce((s, op) => s + (op.valor || 0), 0),
            perdidos,
            conversao: entradas > 0 ? (ganhos.length / entradas) * 100 : 0,
        };
    }, [base]);

    const atual = useMemo(() => medir(janela.de, janela.fim), [medir, janela]);
    const anterior = useMemo(() => medir(janela.deAnterior, janela.fimAnterior), [medir, janela]);

    /* As MESMAS regras do medir(), guardando quem: é o que abre quando se clica no número. */
    const doPeriodo = useMemo(() => ({
        entradas: base.filter(op => dentroDe(op.dataConversaCriada, janela.de, janela.fim)),
        ganhos: base.filter(op => op.idStauts === STATUS_GANHO && dentroDe(op.dataFechamentoVenda, janela.de, janela.fim)),
        perdidos: base.filter(op =>
            op.idStauts === STATUS_PERDIDO && dentroDe(op.dataFechamentoVenda ?? op.dataConversaCriada, janela.de, janela.fim)),
    }), [base, janela]);

    /* ─── Oportunidades abertas, com quanto tempo estão paradas ─────────
       Última movimentação = a anotação mais recente, ou a entrada quando
       não há nenhuma. A base fica escrita na tela: sem anotação, "parada
       há" é só "entrou há", e a lista deixa de ser confiável. */

    const abertas = useMemo(() => {
        const agora = Date.now();
        return base.filter(estaAberta).map(op => {
            const entrada = new Date(op.dataConversaCriada).getTime();
            const ultimaAnotacao = (op.detalhesConversa ?? [])
                .map(d => new Date(d.dataDetalheCriado).getTime())
                .filter(t => Number.isFinite(t))
                .reduce((maior, t) => (t > maior ? t : maior), 0);
            /* A conversa pode estar parada NO FUNIL e viva no WhatsApp: sem isto, alguém que
               respondeu ontem aparecia como "parado há 40 dias" e a lista mandava cobrar quem
               já tinha sido cobrado. A data vem da própria listagem (dataUltimaInteracao). */
            const ultimaMensagem = op.dataUltimaInteracao ? new Date(op.dataUltimaInteracao).getTime() : 0;

            const movimentos = [
                { base: 'entrada' as const, em: entrada },
                { base: 'anotacao' as const, em: Number.isFinite(ultimaAnotacao) ? ultimaAnotacao : 0 },
                { base: 'mensagem' as const, em: Number.isFinite(ultimaMensagem) ? ultimaMensagem : 0 },
            ];
            const ultimo = movimentos.reduce((maior, m) => (m.em > maior.em ? m : maior), movimentos[0]);

            return {
                op,
                base: ultimo.base,
                /* Guardado para a tela dizer DE ONDE veio a data — "parada há" sem base vira
                   número que ninguém confere. */
                desde: ultimo.em,
                dias: Math.max(0, Math.floor((agora - ultimo.em) / DIA)),
            };
        });
    }, [base]);

    /* O corte é escolha de quem olha (ele, 14/10/2026). Era a constante 5, e "quem está parado há
       2 dias" não tinha como ser perguntado em tela nenhuma do produto. */
    const corte = diasDoCorte(semInteracao) ?? 0;
    const rotuloCorte = semInteracao ?? 'qualquer tempo';

    const paradas = useMemo(() =>
        abertas
            .filter(a => a.dias >= corte)
            .sort((a, b) => b.dias - a.dias || (b.op.valor || 0) - (a.op.valor || 0)),
        [abertas, corte]);

    const paradasValor = useMemo(() => paradas.reduce((s, p) => s + (p.op.valor || 0), 0), [paradas]);
    /* A lista aponta quem precisa de ação; sem porta para o registro, a pessoa tinha de
       procurar o nome na Pipeline à mão. Abre a ficha, que é onde a ação acontece — em NOVA ABA
       (pedido dele, 17/10/2026): o Painel é de onde se parte para várias fichas, e trocar de tela
       perdia o painel lateral aberto e a rolagem. Mesmo molde do "Abrir em nova aba" da Pipeline. */
    const abrirOportunidade = (idConversa: number) =>
        window.open(`${window.location.origin}/pipeline/deal/${idConversa}`, '_blank', 'noopener');

    /* ─── A lista por trás de cada número (17/10/2026) ──────────────── */

    const linhaDe = (op: Opportunity, data: 'entrada' | 'fechamento'): LinhaLista => ({
        id: op.idConversa,
        contato: op.nomeContato || 'Sem nome',
        etapa: STATUS_LABEL[op.idStauts] ?? op.descricaoStatus,
        responsavel: op.nomeUsuarioResponsavel?.trim() || null,
        data: data === 'fechamento' ? (op.dataFechamentoVenda ?? op.dataConversaCriada) : op.dataConversaCriada,
        origem: op.atribuicaoAnuncio
            ? `${palavraDaOrigem(op.atribuicaoAnuncio)} · ${nomeDoAnuncio(op.atribuicaoAnuncio)}`
            : textoOrigemMarketing(op.origemMarketing) ?? palavraDoCanal(op.idOrigemOportunidade),
        valor: op.valor || 0,
    });

    const abrirLista = (
        titulo: string,
        contexto: string,
        ops: Opportunity[],
        opcoes: { data?: 'entrada' | 'fechamento'; ordenar?: boolean; nota?: string } = {},
    ) => {
        const data = opcoes.data ?? 'entrada';
        const linhas = ops.map(op => linhaDe(op, data));
        /* Da mais recente para a mais antiga. O Precisa de ação mantém a ordem dele (a mais parada primeiro). */
        if (opcoes.ordenar !== false) {
            linhas.sort((a, b) => new Date(b.data ?? 0).getTime() - new Date(a.data ?? 0).getTime());
        }
        setLista({ titulo, contexto, rotuloData: data === 'fechamento' ? 'Fechou em' : 'Entrou em', nota: opcoes.nota, linhas });
    };

    const paradasPorBase = useMemo(() => ({
        mensagem: paradas.filter(p => p.base === 'mensagem').length,
        anotacao: paradas.filter(p => p.base === 'anotacao').length,
        entrada: paradas.filter(p => p.base === 'entrada').length,
    }), [paradas]);

    /* ─── Carga por responsável ────────────────────────────────────── */

    const carga = useMemo(() => {
        const mapa = new Map<string, { nome: string; abertas: number; paradas: number; semDono: boolean }>();

        abertas.forEach(({ op, dias }) => {
            const semDono = !op.nomeUsuarioResponsavel?.trim();
            const nome = semDono ? SEM_RESPONSAVEL : op.nomeUsuarioResponsavel.trim();
            const atualLinha = mapa.get(nome) ?? { nome, abertas: 0, paradas: 0, semDono };
            atualLinha.abertas += 1;
            if (dias >= corte) atualLinha.paradas += 1;
            mapa.set(nome, atualLinha);
        });

        return [...mapa.values()].sort((a, b) => b.abertas - a.abertas);
    }, [abertas, corte]);

    const cargaMax = useMemo(() => Math.max(1, ...carga.map(c => c.abertas)), [carga]);

    /* ─── Horário de chegada e entradas por dia (30 dias fixos) ─────────
       Não seguem o PERÍODO: o período governa os KPIs de fluxo; daqui para
       baixo a tela mostra o estado da operação agora. Os filtros, sim, valem. */

    const horas = useMemo(() => {
        const inicio = Array.from({ length: 24 }, (_, i) => ({ hora: `${String(i).padStart(2, '0')}h`, entradas: 0 }));
        const limite = inicioDoDia(new Date(Date.now() - 29 * DIA)).getTime();

        base.forEach(op => {
            const d = new Date(op.dataConversaCriada);
            if (Number.isFinite(d.getTime()) && d.getTime() >= limite) inicio[d.getHours()].entradas += 1;
        });

        return inicio;
    }, [base]);

    const resumoHoras = useMemo(() => {
        const total = horas.reduce((s, h) => s + h.entradas, 0);
        if (total === 0) return null;

        let melhorInicio = 0;
        let melhorSoma = -1;
        for (let i = 0; i <= 21; i++) {
            const soma = horas[i].entradas + horas[i + 1].entradas + horas[i + 2].entradas;
            if (soma > melhorSoma) { melhorSoma = soma; melhorInicio = i; }
        }

        const comercial = horas.slice(9, 18).reduce((s, h) => s + h.entradas, 0);
        return {
            pico: `${String(melhorInicio).padStart(2, '0')}h–${String(melhorInicio + 3).padStart(2, '0')}h`,
            comercial: Math.round((comercial / total) * 100),
        };
    }, [horas]);

    const porDia = useMemo(() => {
        const lista: { dia: string; entradas: number }[] = [];
        const indice = new Map<string, number>();

        for (let i = 29; i >= 0; i--) {
            const chave = ddmm(new Date(Date.now() - i * DIA));
            indice.set(chave, lista.length);
            lista.push({ dia: chave, entradas: 0 });
        }

        /* A chave é dd/mm, sem ano: sem este corte, um negócio de 27/09 do ano
           passado cairia na barra de 27/09 deste ano. */
        const limite = inicioDoDia(new Date(Date.now() - 29 * DIA)).getTime();

        base.forEach(op => {
            const d = new Date(op.dataConversaCriada);
            if (!Number.isFinite(d.getTime()) || d.getTime() < limite) return;
            const i = indice.get(ddmm(d));
            if (i !== undefined) lista[i].entradas += 1;
        });

        return lista;
    }, [base]);

    const entradas30 = useMemo(() => porDia.reduce((s, d) => s + d.entradas, 0), [porDia]);

    /* As entradas que os dois gráficos e o bloco de Anúncios contam (mesmo corte do porDia). */
    const entradas30Ops = useMemo(() => {
        const limite = inicioDoDia(new Date(Date.now() - 29 * DIA)).getTime();
        return base.filter(op => {
            const t = new Date(op.dataConversaCriada).getTime();
            return Number.isFinite(t) && t >= limite;
        });
    }, [base]);

    const abrirEntradasDoDia = (dia: string | undefined) => {
        if (!dia) return;
        const ops = entradas30Ops.filter(op => ddmm(new Date(op.dataConversaCriada)) === dia);
        if (ops.length) abrirLista(`Entradas em ${dia}`, 'Entradas por dia', ops);
    };

    const abrirEntradasDaHora = (hora: string | undefined) => {
        if (!hora) return;
        const ops = entradas30Ops.filter(op => `${String(new Date(op.dataConversaCriada).getHours()).padStart(2, '0')}h` === hora);
        if (ops.length) abrirLista(`Entradas às ${hora}`, 'Horário de chegada · últimos 30 dias', ops);
    };

    /* ─── Anúncios (30 dias fixos, como o gráfico ao lado) ──────────────
       Proposta: docs/propostas/conexoes/anuncios-no-painel.html (17/10/2026). Conta pelo anúncio
       citado na 1ª mensagem do lead, então funciona sem a conta de anúncios conectada. Segue os
       filtros, como a tela inteira. */

    const anuncios = useMemo((): LinhaAnuncio[] => {
        const limite = inicioDoDia(new Date(Date.now() - 29 * DIA)).getTime();
        const grupos = new Map<string, Acumulado>();

        base.forEach(op => {
            const a = op.atribuicaoAnuncio;
            /* Publicação e story não são anúncio pago: não entram no painel de Anúncios, como nos relatórios. */
            if (!a || !ehAnuncioPago(a)) return;
            const t = new Date(op.dataConversaCriada).getTime();
            if (!Number.isFinite(t) || t < limite) return;

            const nome = nomeDoAnuncio(a);
            const publicacao = a.anuncioId?.trim() || `atribuicao-${a.atribuicaoAnuncioId}`;
            /* O mesmo anúncio publicado mais de uma vez (mesmo título, texto e tipo; posts
               diferentes) é uma linha só — a O3 tinha o mesmo vídeo duas vezes. Sem texto não
               se junta: título sozinho às vezes é o nome da página e serve para peças
               diferentes (Loja do Menino, 17/10). */
            const corpo = a.anuncioCorpo?.replace(/\s+/g, ' ').trim();
            const chave = corpo ? `${nome}|${corpo}|${a.anuncioTipoMidia ?? ''}` : publicacao;

            const g = grupos.get(chave) ?? {
                chave, nome, tipo: a.anuncioTipoMidia ?? null,
                publicacoes: new Set<string>(), redes: new Set<string>(),
                entraram: 0, ganhos: 0, ganhoValor: 0, maisRecente: 0,
                capa: null, capaEm: 0, link: null, linkEm: 0, ops: [],
            };
            grupos.set(chave, g);

            g.publicacoes.add(publicacao);
            if (a.anuncioRede) g.redes.add(a.anuncioRede);
            g.entraram += 1;
            g.ops.push(op);
            if (op.idStauts === STATUS_GANHO) { g.ganhos += 1; g.ganhoValor += op.valor || 0; }
            g.maisRecente = Math.max(g.maisRecente, t);

            /* Capa e link da publicação que trouxe o lead mais recente. */
            const capa = capaDoAnuncio(a);
            if (capa && t >= g.capaEm) { g.capa = capa; g.capaEm = t; }
            const link = linkDoAnuncio(a);
            if (link && t >= g.linkEm) { g.link = link; g.linkEm = t; }
        });

        return [...grupos.values()]
            .sort((x, y) => y.entraram - x.entraram || y.maisRecente - x.maisRecente)
            .map(g => ({
                chave: g.chave,
                nome: g.nome,
                /* "Instagram · vídeo · 2 publicações" (pedido dele, 17/10/2026: a coluna saiu e o
                   número foi para a linha de apoio, sem clique). Com uma só, nada: seria ruído em toda linha. */
                apoio: [
                    rotuloRedes(g.redes),
                    g.tipo === 'video' ? 'vídeo' : g.tipo === 'image' ? 'imagem' : null,
                    g.publicacoes.size > 1 ? `${g.publicacoes.size} publicações` : null,
                ].filter(Boolean).join(' · '),
                capa: g.capa,
                link: g.link,
                entraram: g.entraram,
                ganhos: g.ganhos,
                ganhoValor: g.ganhoValor,
                ops: g.ops,
            }));
    }, [base]);

    const emAbertoAgora = abertas.length;
    const rotuloPeriodo = `${ddmm(janela.de)} – ${ddmm(janela.fim)}`;
    const paradasVisiveis = mostrarTodasParadas ? paradas : paradas.slice(0, LINHAS_INICIAIS);

    /* ─── Resumo e limpeza do painel ──────────────────────────────── */

    const resumo = [
        responsavel.join(', '),
        etapa.join(', '),
        comQuem.join(', '),
        tipoContato.map(id => opcoesTipoContato.find(o => o.valor === id)?.rotulo ?? SEM_TIPO).join(', '),
        temperatura.join(', '),
        organizacao.join(', '),
        canal.join(', '),
        origemLead.join(', '),
        campanhaTipo.map(o => (o === 'Campanha' ? 'De campanha' : 'Sem campanha')).join(', '),
        campanha.join(', '),
        aguardando ? AGUARDANDO : '',
        semInteracao ? `Parados há ${semInteracao}` : '',
    ].filter(Boolean);
    const ligados = resumo.length;

    const limparCampos = () => {
        setResponsavel([]); setEtapa([]); setComQuem([]); setTipoContato([]); setTemperatura([]);
        setOrganizacao([]); setCanal([]); setOrigemLead([]); setCampanhaTipo([]); setCampanha([]);
        setAguardando(false); setSemInteracao(null);
    };

    /* ─── Blocos de apresentação ──────────────────────────────────── */

    /* Sozinho na linha, o gráfico tem a altura de sempre. Ao lado de Anúncios ele cresce até a
       base da lista, para os dois blocos terminarem juntos sem vão embaixo do gráfico. */
    const blocoEntradas = (esticado: boolean) => {
        const grafico = (altura: number) => (
            <ResponsiveContainer width="100%" height={altura}>
                <BarChart
                    data={porDia} margin={{ top: 18, right: 6, left: 0, bottom: 0 }}
                    className="op-chart-click" onClick={e => abrirEntradasDoDia(e?.activeLabel)}
                >
                    <CartesianGrid stroke={tokens.grid} vertical={false} />
                    <XAxis
                        dataKey="dia" axisLine={false} tickLine={false} interval={6}
                        tick={{ fill: tokens.axis, fontSize: 11 }}
                    />
                    <YAxis
                        axisLine={false} tickLine={false} width={34} allowDecimals={false}
                        tick={{ fill: tokens.axis, fontSize: 11 }}
                    />
                    <Tooltip {...tooltip} formatter={(v: number) => [v, 'Entradas']} />
                    <Bar dataKey="entradas" name="Entradas" fill={tokens.primary} radius={[3, 3, 0, 0]}>
                        {/* Dia sem entrada não ganha "0": o zero repetido vira ruído
                            e a coluna ausente já diz o mesmo. */}
                        <LabelList
                            dataKey="entradas" position="top" offset={6}
                            fill={tokens.muted} fontSize={11}
                            formatter={(v: ReactNode) => (Number(v) > 0 ? v : '')}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        );

        return (
            <div className={`op-card${esticado ? ' is-fill' : ''}`} data-tour="painel-entradas-dia">
                <div className="op-card-head">
                    <div>
                        <h3 className="op-card-title">Entradas por dia</h3>
                        <p className="op-card-sub">últimos 30 dias</p>
                    </div>
                </div>

                {esticado ? (
                    <div className="op-chart-fill" ref={refEntradas}>
                        <div className="op-chart-fill-in">{grafico(Math.max(alturaEntradas, ALTURA_ENTRADAS))}</div>
                    </div>
                ) : grafico(ALTURA_ENTRADAS)}
            </div>
        );
    };

    const anunciosVisiveis = mostrarTodosAnuncios ? anuncios : anuncios.slice(0, LINHAS_ANUNCIOS);
    const leadsDeAnuncio = anuncios.reduce((s, l) => s + l.entraram, 0);

    const linkAnuncio = (l: LinhaAnuncio) => l.link && (
        <a
            className="op-anu-link" href={l.link.url} target="_blank" rel="noopener noreferrer"
            title={l.link.rotulo} aria-label={`${l.link.rotulo}: ${l.nome}`}
        >
            <LuExternalLink size={15} />
        </a>
    );

    const abrirLeadsDoAnuncio = (l: LinhaAnuncio) => () =>
        abrirLista(l.nome, [l.apoio, 'últimos 30 dias'].filter(Boolean).join(' · '), l.ops);
    const abrirGanhosDoAnuncio = (l: LinhaAnuncio) => () =>
        abrirLista(`Ganhos · ${l.nome}`, [l.apoio, 'últimos 30 dias'].filter(Boolean).join(' · '),
            l.ops.filter(op => op.idStauts === STATUS_GANHO), { data: 'fechamento' });

    const ganhosAnuncio = (l: LinhaAnuncio) => (l.ganhos > 0
        ? `${l.ganhos} · ${brl(l.ganhoValor)}`
        : <span className="op-anu-zero">0</span>);

    const blocoAnuncios = (
        <div className="op-card" data-tour="painel-anuncios">
            <div className="op-card-head">
                <div>
                    <h3 className="op-card-title">Anúncios</h3>
                    <p className="op-card-sub">leads que vieram de anúncio · últimos 30 dias</p>
                </div>
                {/* Quanto do gráfico ao lado veio de anúncio: os dois blocos contam a mesma janela. */}
                <div className="op-card-sum">
                    <Porta n={leadsDeAnuncio} rotulo="Ver os leads de anúncio"
                        onClick={() => abrirLista('Leads de anúncio', 'últimos 30 dias', anuncios.flatMap(l => l.ops))}>
                        <b>{leadsDeAnuncio.toLocaleString('pt-BR')}</b>
                    </Porta>
                    {' de '}
                    <Porta n={entradas30} rotulo="Ver as entradas" onClick={() => abrirLista('Entradas', 'últimos 30 dias', entradas30Ops)}>
                        {entradas30.toLocaleString('pt-BR')} {entradas30 === 1 ? 'entrada' : 'entradas'}
                    </Porta>
                </div>
            </div>

            {celular ? (
                <ul className="op-lista">
                    {anunciosVisiveis.map(l => (
                        <li key={l.chave} className="op-anu-item">
                            <CapaAnuncio url={l.capa} />
                            <span className="op-anu-tx">
                                <span className="op-anu-nome">{l.nome}</span>
                                {l.apoio && <span className="op-anu-sub">{l.apoio}</span>}
                                <span className="op-anu-num">
                                    <Porta n={l.entraram} rotulo="Ver os leads deste anúncio" onClick={abrirLeadsDoAnuncio(l)}>
                                        <b>{l.entraram}</b> {l.entraram === 1 ? 'entrou' : 'entraram'}
                                    </Porta>
                                    {' · '}
                                    <Porta n={l.ganhos} rotulo="Ver os ganhos deste anúncio" onClick={abrirGanhosDoAnuncio(l)}>
                                        <b>{l.ganhos}</b> {l.ganhos === 1 ? 'ganho' : 'ganhos'}
                                        {l.ganhos > 0 && ` · ${brl(l.ganhoValor)}`}
                                    </Porta>
                                </span>
                            </span>
                            {linkAnuncio(l)}
                        </li>
                    ))}
                </ul>
            ) : (
                <table className="op-table op-anu-table">
                    <colgroup>
                        <col />
                        <col className="op-anu-col-num" />
                        <col className="op-anu-col-ganho" />
                        <col className="op-anu-col-link" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Anúncio</th>
                            <th className="op-col-right">Entraram</th>
                            <th className="op-col-right">Ganhos</th>
                            <th aria-label="Abrir o anúncio" />
                        </tr>
                    </thead>
                    <tbody>
                        {anunciosVisiveis.map(l => (
                            <tr key={l.chave}>
                                <td>
                                    <span className="op-anu">
                                        <CapaAnuncio url={l.capa} />
                                        <span className="op-anu-tx">
                                            <span className="op-anu-nome" title={l.nome}>{l.nome}</span>
                                            {l.apoio && <span className="op-anu-sub">{l.apoio}</span>}
                                        </span>
                                    </span>
                                </td>
                                <td className="op-col-right">
                                    <Porta n={l.entraram} rotulo="Ver os leads deste anúncio" onClick={abrirLeadsDoAnuncio(l)}>{l.entraram}</Porta>
                                </td>
                                <td className="op-col-right">
                                    <Porta n={l.ganhos} rotulo="Ver os ganhos deste anúncio" onClick={abrirGanhosDoAnuncio(l)}>{ganhosAnuncio(l)}</Porta>
                                </td>
                                <td className="op-col-right">{linkAnuncio(l)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {anuncios.length > LINHAS_ANUNCIOS && (
                <button type="button" className="op-more" onClick={() => setMostrarTodosAnuncios(v => !v)}>
                    {mostrarTodosAnuncios ? 'ver menos' : `ver os ${anuncios.length} →`}
                </button>
            )}

            <p className="op-chart-foot">Pelo anúncio citado na primeira mensagem do lead.</p>
        </div>
    );

    const acoes = (
        <div className="op-period">
            <div className="op-seg" role="group" aria-label="Período">
                {PRESETS.map(p => (
                    <button
                        key={p.id}
                        type="button"
                        className={preset === p.id ? 'is-active' : ''}
                        aria-pressed={preset === p.id}
                        onClick={() => setPreset(p.id)}
                    >
                        {p.label}
                    </button>
                ))}
                {/* Mesma régua, só que livre: um 4º botão em vez de um campo de data no painel de
                    filtros, que faria a tela ter dois controles para a mesma pergunta. */}
                <button
                    type="button"
                    className={preset === 'datas' ? 'is-active' : ''}
                    aria-pressed={preset === 'datas'}
                    onClick={() => setPreset('datas')}
                >
                    <LuCalendar size={12} /> Entre datas
                </button>
            </div>
            {preset === 'datas' ? (
                <div className="op-datas">
                    <label><span>De</span><input type="date" value={dataDe} max={dataAte || undefined} onChange={e => setDataDe(e.target.value)} /></label>
                    <label><span>Até</span><input type="date" value={dataAte} min={dataDe || undefined} onChange={e => setDataAte(e.target.value)} /></label>
                </div>
            ) : (
                <span className="op-range">{rotuloPeriodo}</span>
            )}
        </div>
    );

    const kpi = (
        label: string,
        valor: ReactNode,
        variacao: number | null,
        sufixo: string,
        maiorEhMelhor = true,
        tour?: string,
    ) => {
        const temVariacao = variacao !== null && Number.isFinite(variacao);
        const subiu = temVariacao && variacao! > 0;
        const desceu = temVariacao && variacao! < 0;
        const bom = maiorEhMelhor ? subiu : desceu;
        const classe = !temVariacao || variacao === 0 ? 'is-flat' : bom ? 'is-up' : 'is-down';

        return (
            <div className="op-kpi" data-tour={tour}>
                <div className="op-kpi-label">{label}</div>
                <div className="op-kpi-value">{valor}</div>
                <div className={`op-kpi-delta ${classe}`}>
                    {temVariacao && variacao !== 0 && (subiu ? <LuArrowUp size={12} /> : <LuArrowDown size={12} />)}
                    {temVariacao
                        ? `${umaCasa(Math.abs(variacao!))}${sufixo} vs. período anterior`
                        : 'sem período anterior para comparar'}
                </div>
            </div>
        );
    };

    const variacao = (agora: number, antes: number) => (antes === 0 ? null : ((agora - antes) / antes) * 100);

    const corpo = () => {
        if (isLoading) {
            return (
                <div className="op-panel" aria-busy="true">
                    <div className="op-skeleton-row">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="op-skeleton-block" style={{ height: 84 }} />
                        ))}
                    </div>
                    <div className="op-skeleton-block" style={{ height: 300 }} />
                    <div className="op-grid-2">
                        <div className="op-skeleton-block" style={{ height: 220 }} />
                        <div className="op-skeleton-block" style={{ height: 220 }} />
                    </div>
                </div>
            );
        }

        if (error) return <p className="op-empty">{error}</p>;

        return (
            <div className="op-panel">
                {/* ─── Fluxo do período ─── */}
                <div className="op-kpis">
                    {kpi('Entradas',
                        <Porta n={atual.entradas} rotulo="Ver as entradas do período"
                            onClick={() => abrirLista('Entradas', rotuloPeriodo, doPeriodo.entradas)}>
                            {atual.entradas.toLocaleString('pt-BR')}
                        </Porta>,
                        variacao(atual.entradas, anterior.entradas), '%', true, 'painel-kpi-entradas')}

                    {kpi('Ganhos',
                        <Porta n={atual.ganhos} rotulo="Ver os ganhos do período"
                            onClick={() => abrirLista('Ganhos', rotuloPeriodo, doPeriodo.ganhos, { data: 'fechamento' })}>
                            {atual.ganhos.toLocaleString('pt-BR')}
                            <span className="op-kpi-sub"> · {brl(atual.ganhoValor)}</span>
                        </Porta>,
                        variacao(atual.ganhoValor, anterior.ganhoValor), '%')}

                    {kpi('Perdidos',
                        <Porta n={atual.perdidos} rotulo="Ver os perdidos do período"
                            onClick={() => abrirLista('Perdidos', rotuloPeriodo, doPeriodo.perdidos, { data: 'fechamento' })}>
                            {atual.perdidos.toLocaleString('pt-BR')}
                        </Porta>,
                        variacao(atual.perdidos, anterior.perdidos), '%', false)}

                    {/* A conversão não é uma lista: abre os ganhos, com a conta escrita. */}
                    {kpi('Conversão',
                        <Porta n={atual.ganhos} rotulo="Ver os ganhos que formam a conversão"
                            onClick={() => abrirLista('Conversão', rotuloPeriodo, doPeriodo.ganhos, {
                                data: 'fechamento',
                                nota: `${umaCasa(atual.conversao)}% = ${atual.ganhos} ${atual.ganhos === 1 ? 'ganho' : 'ganhos'} ÷ ${atual.entradas} ${atual.entradas === 1 ? 'entrada' : 'entradas'} do período`,
                            })}>
                            {`${umaCasa(atual.conversao)}%`}
                        </Porta>,
                        anterior.entradas === 0 ? null : atual.conversao - anterior.conversao, ' p.p.')}

                    {/* Estoque não tem seta: a API não guarda histórico de etapa. Segue os filtros,
                        como toda a tela, mas não o período — é o que está aberto AGORA. */}
                    <div className="op-kpi">
                        <div className="op-kpi-label">Em aberto agora</div>
                        <div className="op-kpi-value">
                            <Porta n={emAbertoAgora} rotulo="Ver o que está em aberto"
                                onClick={() => abrirLista('Em aberto agora', 'estoque de hoje', abertas.map(a => a.op))}>
                                {emAbertoAgora.toLocaleString('pt-BR')}
                            </Porta>
                        </div>
                        <div className="op-kpi-stock">estoque · não segue o período</div>
                    </div>
                </div>

                {/* ─── O bloco herói: trabalho que parou de andar ─── */}
                <div className="op-card is-hero">
                    <div className="op-card-head">
                        <div>
                            <h3 className="op-card-title">Precisa de ação</h3>
                            <p className="op-card-sub">
                                {semInteracao
                                    ? `Sem movimento há ${rotuloCorte} ou mais, da mais parada para a menos`
                                    : 'Tudo o que está aberto, da mais parada para a menos'}
                            </p>
                        </div>
                        <div className="op-card-sum">
                            <Porta n={paradas.length} rotulo="Ver todas as que precisam de ação"
                                onClick={() => abrirLista('Precisa de ação', `sem movimento há ${rotuloCorte} ou mais`, paradas.map(p => p.op), { ordenar: false })}>
                                <b>{paradas.length.toLocaleString('pt-BR')} {paradas.length === 1 ? 'oportunidade' : 'oportunidades'}</b>
                                {' · '}{brl(paradasValor)}
                            </Porta>
                        </div>
                    </div>

                    {paradas.length === 0 ? (
                        <p className="op-empty">
                            {semInteracao ? `Nada parado há ${rotuloCorte} ou mais.` : 'Nenhuma oportunidade aberta por aqui.'}
                        </p>
                    ) : (
                        <>
                            {/* No celular a tabela de 5 colunas (460px) vira lista: contato e
                                etapa em cima, responsável, valor e parada há embaixo. Decisão
                                de 30/09/2026 — antes, "Valor" e "Parada há" ficavam fora da
                                tela e o conteúdo todo rolava de lado. */}
                            {celular ? (
                                <ul className="op-lista">
                                    {paradasVisiveis.map(({ op, dias }) => (
                                        <li key={op.idConversa} className="op-lista-item op-row-link" role="link" tabIndex={0}
                                            aria-label={`Abrir a oportunidade de ${op.nomeContato || 'sem nome'}`}
                                            onClick={() => abrirOportunidade(op.idConversa)}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirOportunidade(op.idConversa); } }}>
                                            <span className="op-lista-top">
                                                <span className="op-cell-name">{op.nomeContato || 'Sem nome'}</span>
                                                <span className={`op-pill ${dias >= 14 ? 'is-bad' : 'is-warn'}`}>
                                                    {dias} {dias === 1 ? 'dia' : 'dias'}
                                                </span>
                                            </span>
                                            <span className="op-lista-bot">
                                                <span className="op-cell-muted">{STATUS_LABEL[op.idStauts] ?? op.descricaoStatus}</span>
                                                <span className={op.nomeUsuarioResponsavel?.trim() ? 'op-cell-muted' : 'op-cell-faint'}>
                                                    · {op.nomeUsuarioResponsavel?.trim() || 'sem responsável'}
                                                </span>
                                                <b className="op-lista-valor">{brl(op.valor || 0)}</b>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                            <table className="op-table">
                                <thead>
                                    <tr>
                                        <th>Contato</th>
                                        <th>Etapa</th>
                                        <th>Responsável</th>
                                        <th className="op-col-right">Valor</th>
                                        <th className="op-col-right">Parada há</th>
                                        <th aria-label="Abrir" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {paradasVisiveis.map(({ op, dias }) => (
                                        <tr key={op.idConversa} className="op-row-link" tabIndex={0} role="link"
                                            aria-label={`Abrir a oportunidade de ${op.nomeContato || 'sem nome'}`}
                                            onClick={() => abrirOportunidade(op.idConversa)}
                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirOportunidade(op.idConversa); } }}>
                                            <td className="op-cell-name">{op.nomeContato || 'Sem nome'}</td>
                                            <td className="op-cell-muted">{STATUS_LABEL[op.idStauts] ?? op.descricaoStatus}</td>
                                            <td className={op.nomeUsuarioResponsavel?.trim() ? 'op-cell-muted' : 'op-cell-faint'}>
                                                {op.nomeUsuarioResponsavel?.trim() || 'sem responsável'}
                                            </td>
                                            <td className="op-col-right">{brl(op.valor || 0)}</td>
                                            <td className="op-col-right">
                                                <span className={`op-pill ${dias >= 14 ? 'is-bad' : 'is-warn'}`}>
                                                    {dias} {dias === 1 ? 'dia' : 'dias'}
                                                </span>
                                            </td>
                                            <td className="op-col-go"><LuChevronRight size={15} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            )}

                            {paradas.length > LINHAS_INICIAIS && (
                                <button
                                    type="button"
                                    className="op-more"
                                    onClick={() => setMostrarTodasParadas(v => !v)}
                                >
                                    {mostrarTodasParadas ? 'ver menos' : `ver as ${paradas.length} →`}
                                </button>
                            )}

                            <p className="op-basis">
                                {([
                                    ['mensagem', paradasPorBase.mensagem, 'pela última mensagem'],
                                    ['anotacao', paradasPorBase.anotacao, 'pela última anotação'],
                                    ['entrada', paradasPorBase.entrada, 'pela data de entrada, por não terem nenhuma das duas'],
                                ] as const).filter(([, n]) => n > 0).map(([chave, n, texto], i) => (
                                    <span key={chave}>
                                        {i > 0 && ' · '}
                                        <Porta n={n} rotulo={`Ver as ${n} contadas ${texto.split(',')[0]}`}
                                            onClick={() => abrirLista('Precisa de ação', `contadas ${texto.split(",")[0]}`,
                                                paradas.filter(p => p.base === chave).map(p => p.op), { ordenar: false })}>
                                            {n}
                                        </Porta>
                                        {` ${texto}`}
                                    </span>
                                ))}
                            </p>
                        </>
                    )}
                </div>

                {/* ─── Carga e horário, lado a lado ─── */}
                <div className="op-grid-2">
                    <div className="op-card">
                        <div className="op-card-head">
                            <div>
                                <h3 className="op-card-title">Carga por responsável</h3>
                                <p className="op-card-sub">abertas · paradas</p>
                            </div>
                        </div>

                        {carga.length === 0 ? (
                            <p className="op-empty">Nenhuma oportunidade aberta.</p>
                        ) : carga.map(linha => (
                            <div className="op-load-row" key={linha.nome}>
                                <span className={`op-load-name ${linha.semDono ? 'is-faint' : ''}`} title={linha.nome}>
                                    {linha.nome}
                                </span>
                                <span className="op-load-track">
                                    <span
                                        className={`op-load-fill ${linha.semDono ? 'is-orphan' : ''}`}
                                        style={{ width: `${(linha.abertas / cargaMax) * 100}%` }}
                                    />
                                </span>
                                <span className="op-load-count">
                                    <Porta n={linha.abertas} rotulo={`Ver as abertas de ${linha.nome}`}
                                        onClick={() => abrirLista(linha.nome, 'abertas',
                                            abertas.filter(a => donoDe(a.op) === linha.nome).map(a => a.op))}>
                                        {linha.abertas}
                                    </Porta>
                                    <span className="op-sep"> · </span>
                                    <span className="op-stale">
                                        <Porta n={linha.paradas} rotulo={`Ver as paradas de ${linha.nome}`}
                                            onClick={() => abrirLista(linha.nome, `paradas há ${rotuloCorte} ou mais`,
                                                abertas.filter(a => donoDe(a.op) === linha.nome && a.dias >= corte)
                                                    .sort((a, b) => b.dias - a.dias).map(a => a.op), { ordenar: false })}>
                                            {linha.paradas}
                                        </Porta>
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="op-card">
                        <div className="op-card-head">
                            <div>
                                <h3 className="op-card-title">Horário de chegada</h3>
                                <p className="op-card-sub">quando o lead entra · últimos 30 dias</p>
                            </div>
                        </div>

                        <ResponsiveContainer width="100%" height={180}>
                            <AreaChart
                                data={horas} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}
                                className="op-chart-click" onClick={e => abrirEntradasDaHora(e?.activeLabel)}
                            >
                                <defs>
                                    <linearGradient id="opHoras" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={tokens.primary} stopOpacity={0.22} />
                                        <stop offset="100%" stopColor={tokens.primary} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke={tokens.grid} vertical={false} />
                                <XAxis
                                    dataKey="hora" axisLine={false} tickLine={false} interval={5}
                                    tick={{ fill: tokens.axis, fontSize: 11 }}
                                />
                                <YAxis
                                    axisLine={false} tickLine={false} width={34} allowDecimals={false}
                                    tick={{ fill: tokens.axis, fontSize: 11 }}
                                />
                                <Tooltip {...tooltip} formatter={(v: number) => [v, 'Entradas']} />
                                <Area
                                    type="monotone" dataKey="entradas" name="Entradas"
                                    stroke={tokens.primary} strokeWidth={2} fill="url(#opHoras)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>

                        {resumoHoras && (
                            <p className="op-chart-foot">
                                Pico às <b>{resumoHoras.pico}</b>. {resumoHoras.comercial}% das entradas entre 9h e 18h.
                            </p>
                        )}
                    </div>
                </div>

                {/* ─── Volume de entrada e, para quem anuncia, de qual anúncio ─── */}
                {anuncios.length > 0 ? (
                    <div className="op-grid-2 is-stretch">
                        {blocoEntradas(true)}
                        {blocoAnuncios}
                    </div>
                ) : blocoEntradas(false)}
            </div>
        );
    };

    return (
        <div className="page-shell op-screen lk-screen">
            <PageHeader
                title="Painel Operacional"
                subtitle="O que entrou, quem está atendendo e o que parou de andar"
                actions={acoes}
            />
            <div className="page-body">
                {/* A faixa de filtros vem antes dos números: é ela que diz de QUEM são. */}
                <div className="lk-filters op-filters">
                    <FiltrosBotao ref={filtrosAncora} ligados={ligados} aberto={filtrosAbertos} onClick={() => setFiltrosAbertos(a => !a)} />
                    <FiltrosResumo partes={resumo} />
                    {!isLoading && !error && (
                        <span className="lk-count">
                            <Porta n={base.length} rotulo="Ver as oportunidades da tela"
                                onClick={() => abrirLista('Oportunidades', 'todas as da tela', base)}>
                                {base.length === todas.length
                                    ? `${todas.length} ${todas.length === 1 ? 'oportunidade' : 'oportunidades'}`
                                    : `${base.length} de ${todas.length} oportunidades`}
                            </Porta>
                        </span>
                    )}
                </div>

                <FiltrosPainel
                    open={filtrosAbertos}
                    onClose={() => setFiltrosAbertos(false)}
                    subtitulo={celular ? `${base.length} de ${todas.length}` : `Aplicados na hora · ${base.length} de ${todas.length} oportunidades`}
                    ligados={ligados}
                    onLimpar={limparCampos}
                    celular={celular}
                    verRotulo={`Ver ${base.length} ${base.length === 1 ? 'oportunidade' : 'oportunidades'}`}
                    ancora={filtrosAncora}
                >
                    {/* Quem está com o trabalho. */}
                    <FiltroColuna>
                        <FiltroCampo rotulo="Responsável" onLimpar={responsavel.length ? () => setResponsavel([]) : undefined}>
                            <FiltroMulti opcoes={opcoesResponsavel} valores={responsavel} onChange={setResponsavel} />
                        </FiltroCampo>
                        {/* O avesso de "responsável": quem ainda está com o robô. A etapa 8 (IA
                            encerrada) conta como humano — é exatamente a que espera gente. */}
                        <FiltroCampo rotulo="Com quem está" onLimpar={comQuem.length ? () => setComQuem([]) : undefined}>
                            <FiltroChips opcoes={COM_QUEM} valores={comQuem} onChange={setComQuem} />
                        </FiltroCampo>
                        <FiltroCampo rotulo="Etapa" onLimpar={etapa.length ? () => setEtapa([]) : undefined}>
                            <FiltroMulti opcoes={opcoesEtapa} valores={etapa} onChange={setEtapa} vazio="Todas" />
                        </FiltroCampo>
                        {opcoesOrganizacao.length > 1 && (
                            <FiltroCampo rotulo="Organização" onLimpar={organizacao.length ? () => setOrganizacao([]) : undefined}>
                                <FiltroMulti opcoes={opcoesOrganizacao} valores={organizacao} onChange={setOrganizacao} vazio="Todas" />
                            </FiltroCampo>
                        )}
                    </FiltroColuna>

                    {/* Que gente é esta e por onde chegou. */}
                    <FiltroColuna>
                        {opcoesTemperatura.length > 1 && (
                            <FiltroCampo rotulo="Temperatura" onLimpar={temperatura.length ? () => setTemperatura([]) : undefined}>
                                <FiltroChips opcoes={opcoesTemperatura} valores={temperatura} onChange={setTemperatura} />
                            </FiltroCampo>
                        )}
                        {usaTipoContato && opcoesTipoContato.length > 0 && (
                            <FiltroCampo rotulo="Tipo de contato" onLimpar={tipoContato.length ? () => setTipoContato([]) : undefined}>
                                <FiltroMulti opcoes={opcoesTipoContato} valores={tipoContato} onChange={setTipoContato} vazio="Todos" />
                            </FiltroCampo>
                        )}
                        {opcoesCanal.length > 1 && (
                            <FiltroCampo rotulo="Origem" onLimpar={canal.length ? () => setCanal([]) : undefined}>
                                <FiltroChips opcoes={opcoesCanal} valores={canal} onChange={setCanal} />
                            </FiltroCampo>
                        )}
                        {/* "Origem" ali em cima é o CANAL (onde se conversa). Este é de ONDE a pessoa
                            veio — só aparece quando alguma oportunidade tem origem rastreada. */}
                        {opcoesOrigemLead.length > 0 && (
                            <FiltroCampo rotulo="Origem do lead" onLimpar={origemLead.length ? () => setOrigemLead([]) : undefined}>
                                <FiltroMulti opcoes={opcoesOrigemLead} valores={origemLead} onChange={setOrigemLead} vazio="Todas" />
                            </FiltroCampo>
                        )}
                    </FiltroColuna>

                    {/* Tempo: as duas perguntas que o painel existe para responder. A campanha vem
                        junto para a terceira coluna não terminar 150px acima das outras duas — com
                        dois campos só, sobrava vão branco embaixo dela. */}
                    <FiltroColuna>
                        <FiltroCampo rotulo="Sem interação há mais de" onLimpar={semInteracao ? () => setSemInteracao(null) : undefined}>
                            <FiltroChips
                                unico
                                opcoes={PARADOS.map(p => ({ valor: p.valor }))}
                                valores={semInteracao ? [semInteracao] : []}
                                onChange={v => setSemInteracao(v[0] ?? null)}
                            />
                        </FiltroCampo>
                        {/* Quem falou por último foi o cliente. Diferente de "parado há N dias": um
                            cliente que perguntou ontem e não foi respondido não está parado, está
                            esperando — e é o mais caro dos dois. */}
                        <FiltroCampo rotulo="Situação">
                            <FiltroChips
                                opcoes={[{ valor: AGUARDANDO }]}
                                valores={aguardando ? [AGUARDANDO] : []}
                                onChange={v => setAguardando(v.includes(AGUARDANDO))}
                            />
                        </FiltroCampo>
                        <FiltroCampo rotulo="Campanha" onLimpar={campanhaTipo.length ? () => { setCampanhaTipo([]); setCampanha([]); } : undefined}>
                            <FiltroChips
                                opcoes={CAMPANHAS}
                                valores={campanhaTipo}
                                onChange={v => { setCampanhaTipo(v); if (!v.includes('Campanha')) setCampanha([]); }}
                            />
                        </FiltroCampo>
                        {campanhaTipo.includes('Campanha') && opcoesCampanha.length > 0 && (
                            <FiltroCampo rotulo="Qual campanha" onLimpar={campanha.length ? () => setCampanha([]) : undefined}>
                                <FiltroMulti opcoes={opcoesCampanha} valores={campanha} onChange={setCampanha} vazio="Todas" />
                            </FiltroCampo>
                        )}
                    </FiltroColuna>
                </FiltrosPainel>

                {corpo()}

                {/* Fora do .op-panel de propósito: ele é container (inline-size), e container
                    vira o bloco de referência do overlay fixo — o painel abriria preso nele. */}
                {/* As novidades desta tela, nos balões. Empresa sem nenhuma oportunidade não vê:
                    os números estão zerados e não há o que mostrar (pedido dele, 17/10/2026). */}
                <TourNovidade tour={TOUR_PAINEL} pronta={!isLoading && !error && todas.length > 0} />

                <PainelLista
                    lista={lista}
                    comFiltros={base.length !== todas.length}
                    celular={celular}
                    onClose={() => setLista(null)}
                    onAbrir={abrirOportunidade}
                />
            </div>
        </div>
    );
};

export default Dashboard;
